import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import AppLayout from "../../components/layout/AppLayout";
import { dashboardService } from "../../services/dashboardService";

const Dashboard = () => {
  // --- MEMORI (STATE): Menyimpan angka-angka statistik dashboard ---
  const [loading, setLoading] = useState(true); // status loading (sedang mengambil data)
  const [metrics, setMetrics] = useState({
    totalSales: 0, // total uang masuk dari penjualan
    activeSkus: 0, // jumlah jenis barang yang aktif (tidak dihapus)
    stockAlerts: 0, // jumlah barang yang stoknya menipis
    recentSales: [], // daftar 5 penjualan terbaru
    lowStockItems: [], // daftar barang yang perlu restock
  });

  // --- LOGIKA OTOMATIS: Ambil data saat halaman pertama kali dibuka ---
  useEffect(() => {
    fetchDashboardData();
  }, []);

  /**
   * --- LOGIKA: Mengambil Data dari Database ---
   * Fungsi ini memanggil 'Layanan Dashboard' untuk mengumpulkan semua statistik.
   */
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const data = await dashboardService.getDashboardMetrics();
      setMetrics(data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * --- FUNGSI PEMBANTU: Format Mata Uang ---
   * Mengubah angka biasa (10000) menjadi format Rupiah (Rp 10.000,00).
   */
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
    }).format(value);
  };

  return (
    <AppLayout pageTitle="Dashboard">
      <div className="max-w-7xl mx-auto space-y-6 md:space-y-10">
        {/* BAGIAN TAMPILAN: Judul Halaman */}
        <section className="flex flex-col md:flex-row md:justify-between md:items-end gap-4">
          <div className="space-y-1">
            <h2 className="font-headline text-2xl md:text-3xl font-extrabold text-on-surface tracking-tight leading-tight">
              Dashboard
            </h2>
            <p className="font-body text-xs md:text-sm text-on-surface-variant">
              Ringkasan performa dan status operasional hari ini.
            </p>
          </div>
        </section>

        {/* BAGIAN TAMPILAN: Kartu Angka (Metrics) */}
        <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-12 gap-4 md:gap-6">
          {/* Kartu: Total Penjualan */}
          <div className="col-span-1 sm:col-span-2 xl:col-span-6 bg-surface-container-lowest p-6 md:p-8 rounded-[2rem] custom-shadow flex flex-col justify-between overflow-hidden relative group border border-outline-variant/10">
            <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
              <span className="material-symbols-outlined text-6xl md:text-8xl">
                payments
              </span>
            </div>
            <div className="relative z-10">
              <span className="font-label text-[10px] font-bold uppercase tracking-[0.2em] text-on-surface-variant">
                Total Penjualan
              </span>
              <h3 className="font-headline text-2xl xs:text-3xl md:text-4xl lg:text-5xl font-black text-primary mt-2 flex flex-wrap items-baseline gap-1">
                {loading ? "..." : formatCurrency(metrics.totalSales)}
              </h3>
            </div>
            <div className="mt-6 md:mt-8 flex items-center gap-2 relative z-10">
              <span className="flex items-center text-primary font-bold text-[10px] md:text-xs uppercase tracking-wider">
                <span className="material-symbols-outlined text-sm mr-1">
                  trending_up
                </span>
                Semua Transaksi
              </span>
            </div>
          </div>

          {/* Kartu: Barang Aktif */}
          <div className="col-span-1 xl:col-span-3 bg-surface-container p-6 md:p-8 rounded-[2rem] flex flex-col justify-between transition-all hover:bg-surface-container-high border border-outline-variant/10">
            <div>
              <span className="font-label text-[10px] font-bold uppercase tracking-[0.2em] text-on-surface-variant">
                SKU Aktif
              </span>
              <h3 className="font-headline text-2xl md:text-3xl font-bold text-on-surface mt-2">
                {loading ? "..." : metrics.activeSkus}
              </h3>
            </div>
            <div className="mt-6">
              <div className="h-1.5 w-full bg-outline-variant/30 rounded-full overflow-hidden">
                <div className="h-full bg-secondary-fixed-dim w-3/4 rounded-full"></div>
              </div>
              <p className="text-[10px] mt-3 text-on-surface-variant font-bold uppercase tracking-wider">
                Katalog Aktif
              </p>
            </div>
          </div>

          {/* Kartu: Peringatan Stok Rendah */}
          <div className={`col-span-1 xl:col-span-3 p-6 md:p-8 rounded-[2rem] flex flex-col justify-between transition-all border border-outline-variant/10 ${metrics.stockAlerts > 0 ? "bg-tertiary-container/30 ring-2 ring-tertiary/10" : "bg-surface-container hover:bg-surface-container-high"}`}>
            <div>
              <span
                className={`font-label text-[10px] font-bold uppercase tracking-[0.2em] ${metrics.stockAlerts > 0 ? "text-tertiary" : "text-on-surface-variant"}`}
              >
                Peringatan Stok
              </span>
              <h3
                className={`font-headline text-2xl md:text-3xl font-bold mt-2 ${metrics.stockAlerts > 0 ? "text-tertiary" : "text-on-surface"}`}
              >
                {loading ? "..." : metrics.stockAlerts}
              </h3>
            </div>
            <div
              className={`mt-6 flex items-center gap-2 font-bold text-[10px] uppercase tracking-wider ${metrics.stockAlerts > 0 ? "text-tertiary" : "text-on-surface-variant"}`}
            >
              <span className="material-symbols-outlined text-sm">
                {metrics.stockAlerts > 0 ? "priority_high" : "check_circle"}
              </span>
              {metrics.stockAlerts > 0 ? "Butuh Tindakan" : "Semua Aman"}
            </div>
          </div>
        </section>

        {/* BAGIAN TAMPILAN: Tabel Riwayat & Peringatan Detail */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          {/* TABEL: 5 Penjualan Terbaru */}
          <div className="col-span-12 lg:col-span-8 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-headline text-lg font-bold text-on-surface">
                Riwayat Penjualan
              </h4>
              <NavLink
                to="/sales-history"
                className="text-xs font-semibold text-primary hover:underline"
              >
                Lihat Riwayat
              </NavLink>
            </div>
            <div className="bg-surface-container-lowest rounded-xl custom-shadow overflow-hidden">
              {/* TAMPILAN MEJA (Desktop): Terlihat di Layar Lebar/Laptop */}
              <div className="overflow-x-auto hidden md:block">
                <table className="w-full text-left border-collapse min-w-[500px]">
                  <thead>
                    <tr className="border-b border-outline-variant/20">
                      <th className="px-6 py-4 font-label text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
                        ID Pesanan
                      </th>
                      <th className="px-6 py-4 font-label text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
                        Pelanggan
                      </th>
                      <th className="px-6 py-4 font-label text-[10px] font-bold uppercase tracking-wider text-on-surface-variant text-right">
                        Jumlah
                      </th>
                      <th className="px-6 py-4 font-label text-[10px] font-bold uppercase tracking-wider text-on-surface-variant text-center">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline-variant/10">
                    {loading ? (
                      <tr>
                        <td
                          colSpan="4"
                          className="px-6 py-12 text-center text-sm text-on-surface-variant"
                        >
                          Memuat...
                        </td>
                      </tr>
                    ) : metrics.recentSales.length === 0 ? (
                      <tr>
                        <td
                          colSpan="4"
                          className="px-6 py-12 text-center text-sm text-on-surface-variant"
                        >
                          Belum ada penjualan.
                        </td>
                      </tr>
                    ) : (
                      metrics.recentSales.map((sale) => (
                        <tr
                          key={sale.id}
                          className="hover:bg-surface-bright transition-colors"
                        >
                          <td className="px-6 py-4 text-xs font-mono font-medium text-on-surface">
                            #{sale.transaction_id}
                          </td>
                          <td className="px-6 py-4 text-sm text-on-surface-variant">
                            {sale.customer_name || "Anonymous"}
                          </td>
                          <td className="px-6 py-4 text-sm font-bold text-on-surface text-right">
                            {formatCurrency(sale.total)}
                          </td>
                          <td className="px-6 py-4 text-center whitespace-nowrap">
                            <span
                              className={`inline-flex px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-tighter whitespace-nowrap ${
                                sale.status === "Completed"
                                  ? "bg-secondary-container text-on-secondary-container"
                                  : "bg-surface-container-highest text-on-surface-variant"
                              }`}
                            >
                              {sale.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* TAMPILAN KARTU (Mobile): Muncul Otomatis di Layar HP */}
              <div className="grid grid-cols-1 gap-3 p-4 md:hidden">
                {loading ? (
                  <div className="text-center py-8 text-sm text-on-surface-variant">
                    Memuat...
                  </div>
                ) : metrics.recentSales.length === 0 ? (
                  <div className="text-center py-8 text-sm text-on-surface-variant">
                    Belum ada penjualan.
                  </div>
                ) : (
                  metrics.recentSales.map((sale) => (
                    <div
                      key={sale.id}
                      className="bg-surface-container-low rounded-xl p-4 border border-outline-variant/20 flex flex-col gap-3"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-xs font-mono font-bold text-primary">
                            #{sale.transaction_id}
                          </p>
                          <p className="text-sm font-bold text-on-surface mt-1">
                            {sale.customer_name || "Anonymous"}
                          </p>
                        </div>
                        <span
                          className={`px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider whitespace-nowrap ${
                            sale.status === "Completed"
                              ? "bg-secondary-container text-on-secondary-container"
                              : "bg-surface-container-highest text-on-surface-variant"
                          }`}
                        >
                          {sale.status}
                        </span>
                      </div>
                      <div className="flex justify-between items-center pt-3 border-t border-outline-variant/10">
                        <span className="text-[10px] font-bold text-outline uppercase tracking-widest">
                          Total
                        </span>
                        <span className="text-sm font-black text-on-surface">
                          {formatCurrency(sale.total)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* BAGIAN TAMPILAN: Samping (Daftar Peringatan Stok) */}
          <div className="col-span-12 lg:col-span-4 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-headline text-lg font-bold text-on-surface">
                Peringatan Prioritas
              </h4>
              <span className="material-symbols-outlined text-tertiary">
                inventory
              </span>
            </div>
            <div className="bg-surface-container-low rounded-xl p-4 md:p-6 space-y-4 border border-outline-variant/30">
              {loading ? (
                <p className="text-xs text-on-surface-variant text-center py-4">
                  Memeriksa Stok...
                </p>
              ) : metrics.lowStockItems.length === 0 ? (
                <div className="p-4 bg-surface-container-lowest rounded-lg border-l-4 border-primary">
                  <p className="text-xs text-on-surface-variant">
                    Semua stok mencukupi.
                  </p>
                </div>
              ) : (
                metrics.lowStockItems.map((item) => (
                  <div
                    key={item.sku}
                    className="flex items-start gap-3 p-4 bg-surface-container-lowest rounded-lg border-l-4 border-tertiary-container shadow-sm"
                  >
                    <div className="flex-1">
                      <h5 className="text-sm font-bold text-on-surface">
                        {item.name}
                      </h5>
                      <p className="text-[10px] text-on-surface-variant mt-1 font-mono uppercase">
                        {item.sku}
                      </p>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-[10px] font-black uppercase text-tertiary">
                          {item.stock_level === 0
                            ? "Out of Stock"
                            : `${item.stock_level} Unit Remaining`}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      </div>
    </AppLayout>
  );
};

export default Dashboard;
